'use client';

import { FC, useCallback, useState } from 'react';

import { useAuthentication, UserGroups } from '@ravago/shared/authentication';
import { CustomerUtils } from '@ravago/shared/next-data-access/client';
import { Customer } from '@ravago/shared/page-data/models/features';
import { Alert } from '@ravago/shared/radiance/components/alert/components/Alert/Alert';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Dialog } from '@ravago/shared/radiance/components/dialog/components/Dialog/Dialog';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { Radio } from '@ravago/shared/radiance/components/radio/components/Radio/Radio';
import { RadioGroup } from '@ravago/shared/radiance/components/radio/components/RadioGroup/RadioGroup';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import { useQuery, useMutation } from '@tanstack/react-query';

import debounce from 'lodash/debounce';

import { DataTestSelector } from '../../../utils/format/format.utils';

interface Props {
   open: boolean;
   currentCustomer: Customer | undefined;
   onDialogClose?: () => void;
}

const labels = {
   customerSwitchTitle: 'page-consumer.header.customer-switch-dialog.header-switch',
   customerEnableTitle: 'page-consumer.header.customer-switch-dialog.header-enable',
   cancelAction: 'page-consumer.header.customer-switch-dialog.cancel',
   switchAction: 'page-consumer.header.customer-switch-dialog.switch-customer',
   switchingAction: 'page-consumer.header.customer-switch-dialog.switching-customer',
   enableAction: 'page-consumer.header.customer-switch-dialog.enable-customer',
   enablingAction: 'page-consumer.header.customer-switch-dialog.enabling-customer',
   onSwitchErrorTitle: 'page-consumer.header.customer-switch-dialog.on-switch-error-title',
   onSwitchErrorText: 'page-consumer.header.customer-switch-dialog.on-switch-error-text',
   onEnableSuccessTitle: 'page-consumer.header.customer-switch-dialog.on-enable-success-title',
   onEnableSuccessText: 'page-consumer.header.customer-switch-dialog.on-enable-success-text',
   onEnableErrorTitle: 'page-consumer.header.customer-switch-dialog.on-enable-error-title',
   onEnableErrorText: 'page-consumer.header.customer-switch-dialog.on-enable-error-text'
};

export const CustomerSwitchDialog: FC<Props> = ({ open, currentCustomer, onDialogClose }) => {
   const translate = useTranslations();
   const [selectedCustomer, setSelectedCustomer] = useState<number>(-1);
   const [searchTerm, setSearchTerm] = useState<string>('');
   const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
   const [hasSuccess, setHasSuccess] = useState<{ title: string; text: string } | null>(null);
   const [hasError, setHasError] = useState<{ title: string; text: string } | null>(null);

   const customerDisabled = !currentCustomer?.customerPortalEnabled;

   const { user, accessToken, refreshToken } = useAuthentication();

   const { phone } = useBreakpoint();

   const canSwitchCustomer =
      user && (user.groups.includes(UserGroups.ravagoSalesRepresentative) || user.groups.includes(UserGroups.ravagoGlobalGroupAdmin));

   const hasCustomer = user && user.groups.includes(UserGroups.customerPortal);

   const debounceSearch = useCallback(debounce(setDebouncedSearchTerm, 500), []);

   const customersQuery = useQuery({
      queryKey: ['customers', debouncedSearchTerm, open],
      queryFn: async () => {
         if (!open) return [];

         if (debouncedSearchTerm && debouncedSearchTerm.length > 1) {
            return CustomerUtils.getAllCustomers(debouncedSearchTerm);
         }

         return CustomerUtils.getAllCustomers('', '4');
      },
      enabled: !!accessToken && open
   });

   const setCustomerMutation = useMutation({
      mutationFn: (id: number) => CustomerUtils.setCustomer(id),
      onSuccess: (success) => {
         if (success) {
            refreshToken()
               .then(() => window.location.reload())
               .catch(() => {
                  setHasError({ title: translate(labels.onSwitchErrorTitle), text: translate(labels.onSwitchErrorText) });
               });
         } else {
            setHasError({ title: translate(labels.onSwitchErrorTitle), text: translate(labels.onSwitchErrorText) });
         }
      },
      onError: () => {
         setHasError({ title: translate(labels.onSwitchErrorTitle), text: translate(labels.onSwitchErrorText) });
      }
   });

   const enableCustomerMutation = useMutation({
      mutationFn: () => CustomerUtils.enableCustomer(),
      onSuccess: (success) => {
         if (success) {
            setHasSuccess({ title: translate(labels.onEnableSuccessTitle), text: translate(labels.onEnableSuccessText) });
            setTimeout(() => window.location.reload(), 4000);
         } else {
            setHasError({ title: translate(labels.onEnableErrorTitle), text: translate(labels.onEnableErrorText) });
         }
      },
      onError: () => {
         setHasError({ title: translate(labels.onEnableErrorTitle), text: translate(labels.onEnableErrorText) });
      }
   });

   const handleSearch = (value: string) => {
      setSearchTerm(value);
      debounceSearch(value);
   };

   const handleSetCustomer = (id: number) => {
      setHasError(null);
      setCustomerMutation.mutate(id);
   };

   const handleEnableCustomer = () => {
      setHasError(null);
      setHasSuccess(null);
      enableCustomerMutation.mutate();
   };

   const handleDialogClose = () => {
      onDialogClose?.();
      setSelectedCustomer(-1);
   };

   if (!hasCustomer) return null;

   return (
      <Dialog
         open={open}
         disableBackdrop={customerDisabled}
         title={!customerDisabled ? translate(labels.customerSwitchTitle) : translate(labels.customerEnableTitle)}
         closable={!customerDisabled}
         actions={
            <>
               {!customerDisabled && !setCustomerMutation.isPending && !enableCustomerMutation.isPending && (
                  <Button variant="secondary" attributes={{ ...DataTestSelector('customer-switch-dialog-button-close') }} onClick={handleDialogClose}>
                     {translate(labels.cancelAction)}
                  </Button>
               )}
               <Button
                  attributes={{ ...DataTestSelector('customer-switch-dialog-button-switch') }}
                  disabled={(!customerDisabled && selectedCustomer === -1) || setCustomerMutation.isPending || enableCustomerMutation.isPending}
                  onClick={() => {
                     if ((selectedCustomer && !customerDisabled) || selectedCustomer !== -1) handleSetCustomer(selectedCustomer);
                     if (customerDisabled && selectedCustomer === -1) handleEnableCustomer();
                  }}
               >
                  <Box gap="sm">
                     {(setCustomerMutation.isPending || enableCustomerMutation.isPending) && <Spinner size="small" />}

                     {customerDisabled && selectedCustomer === -1 ? (
                        <>
                           {!enableCustomerMutation.isPending && translate(labels.enableAction)}
                           {enableCustomerMutation.isPending && translate(labels.enablingAction)}
                        </>
                     ) : null}

                     {(customerDisabled && selectedCustomer !== -1) || !customerDisabled ? (
                        <>
                           {!setCustomerMutation.isPending && translate(labels.switchAction)}
                           {setCustomerMutation.isPending && translate(labels.switchingAction)}
                        </>
                     ) : null}
                  </Box>
               </Button>
            </>
         }
         onDialogClose={handleDialogClose}
         closeOnOutsideClick={false}
      >
         <Box direction="column" width={phone ? '100%' : '560px'} margin={{ top: 'xs' }}>
            {canSwitchCustomer ? (
               <>
                  <InputField
                     id="search-customers"
                     label={translate('page-consumer.header.customer-switch-dialog.search')}
                     trailingIcon="search"
                     value={searchTerm}
                     onChange={handleSearch}
                  />

                  {hasError && (
                     <Alert headline={hasError.title} variant="error">
                        {hasError.text}
                     </Alert>
                  )}

                  {hasSuccess && (
                     <Alert headline={hasSuccess.title} variant="success">
                        {hasSuccess.text}
                     </Alert>
                  )}

                  {customerDisabled && (
                     <Alert headline={translate('page-consumer.header.customer-switch-dialog.on-disabled-title')} variant="warning">
                        {translate('page-consumer.header.customer-switch-dialog.on-disabled-text', { customer: currentCustomer?.name ?? '' })}
                     </Alert>
                  )}

                  {customersQuery.isFetching && (
                     <Box height="220px" items="center" justify="center">
                        <Spinner />
                     </Box>
                  )}

                  {!customersQuery.isFetching && (
                     <Box maxHeight="440px" overflowY="auto">
                        <RadioGroup value={selectedCustomer} onChange={setSelectedCustomer}>
                           {currentCustomer && !!customersQuery.data?.length && (
                              <Radio
                                 attributes={{ ...DataTestSelector('customer-switch-dialog-option', currentCustomer.name) }}
                                 name="test"
                                 disabled={currentCustomer.customerPortalEnabled || setCustomerMutation.isPending || enableCustomerMutation.isPending}
                                 key={-1}
                                 id="-1"
                                 value={-1}
                              >
                                 {currentCustomer.name}
                              </Radio>
                           )}

                           {customersQuery.data?.map(
                              (customer) =>
                                 customer.name !== currentCustomer?.name && (
                                    <Radio
                                       disabled={setCustomerMutation.isPending || enableCustomerMutation.isPending}
                                       attributes={{ ...DataTestSelector('customer-switch-dialog-option', customer.name) }}
                                       name="test"
                                       key={customer.id}
                                       id={customer.id.toString()}
                                       value={customer.id}
                                    >
                                       {customer.name}
                                    </Radio>
                                 )
                           )}
                        </RadioGroup>
                     </Box>
                  )}
               </>
            ) : (
               <Alert headline={translate('page-consumer.header.customer-switch-dialog.on-not-allowed-title')} variant="warning">
                  {translate('page-consumer.header.customer-switch-dialog.on-not-allowed-text')}
               </Alert>
            )}
         </Box>
      </Dialog>
   );
};
